import { createContext, ReactNode, use, useCallback, useEffect, useId, useRef, useState } from 'react';
import { confirmable, ConfirmDialog, createConfirmation } from 'react-confirm';
import styled from 'styled-components';
import {
  CustomMusic,
  CustomMusicManifest,
  Hymn,
  PracticeVideos,
  SongPracticeVideos,
  SongSlug,
} from '../../../common/src/model.ts';
import * as server_api from '../../../common/src/server_api.ts';
import CloseIcon from '../assets/close.svg?react';
import TrashIcon from '../assets/trash.svg?react';
import { useCurrentOrg } from '../data/use_current_org.tsx';
import { recordRecentlyAddedSong, recordRecentlyDeletedSong, useCustomMusic } from '../data/use_custom_music.tsx';
import { useUserAttributes } from '../data/use_user_attributes.ts';
import { Button } from '../pages/onboarding/button.tsx';
import { Message } from '../pages/onboarding/common.tsx';
import { useSongVisibility } from '../pages/onboarding/hidden_song_check_box.tsx';
import { alert, confirm } from './confirm.tsx';
import { ModalSpinner } from './modal_spinner.tsx';
import { getHymnFromSlug, isCustomSong } from './path.ts';
import { isSamePracticeVideos, PracticeVideosSection, usePracticeVideoFields } from './practice_videos_section.tsx';
import { Pencil } from './shared.tsx';
import { Spinner } from './spinner.tsx';
import { useHoldAppReload } from './use_hold_app_reload.ts';
import { holdAppReload } from './user_activity.ts';

const EditButtonContext = createContext<{ hasPermission: boolean } | undefined>(undefined);

export function EditButtonContextProvider({ children }: { children: ReactNode }) {
  const { isInternalUser, isOrgAdmin } = useUserAttributes();
  return <EditButtonContext value={{ hasPermission: isInternalUser || isOrgAdmin }}>{children}</EditButtonContext>;
}

enum UploadStatus {
  Before = 'before',
  Uploading = 'uploading',
  Succeeded = 'succeeded',
  Failed = 'failed',
}

let busyUploading = false;

// The org's practice video map with this song's entry replaced. The whole map goes to the server, so the cached
// org stays complete (updateOrgInfo merges fields shallowly), and the server merges it song by song: a song with
// no video left is sent as {}, which is what removes its videos there (mergeOrgUpsert).
function withSongPracticeVideos(
  practiceVideos: SongPracticeVideos | undefined,
  songSlug: SongSlug,
  videos: PracticeVideos,
): SongPracticeVideos {
  return { ...practiceVideos, [songSlug]: videos };
}

const EditCustomSong: ConfirmDialog<
  { customMusic: CustomMusic; customMusicManifest: CustomMusicManifest | undefined; songSlug: SongSlug },
  { songSlug?: SongSlug }
> = ({ customMusic, customMusicManifest, songSlug, proceed, show }) => {
  const onClose = useCallback(() => proceed({}), [proceed]);
  return (
    <EditSongDialog
      customMusic={customMusic}
      customMusicManifest={customMusicManifest}
      songSlug={songSlug}
      show={show}
      onClose={onClose}
    />
  );
};

/**
 * The edit-song pop-up for a custom song (the pencil in Our Hymnal): name, psalm number, practice videos
 * (GitHub issue #4041), hide in Our Hymnal, replace the PDF, delete the song. `isInline` renders it in the page
 * flow rather than as a modal, for Storybook.
 */
export function EditSongDialog({
  customMusic,
  customMusicManifest,
  songSlug,
  show,
  onClose,
  isInline = false,
}: {
  customMusic: CustomMusic;
  customMusicManifest: CustomMusicManifest | undefined;
  songSlug: SongSlug;
  show: boolean;
  onClose: () => void;
  isInline?: boolean;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (isInline) {
      return;
    }
    if (show) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [show, dialogRef, isInline]);

  const songNameInputRef = useRef<HTMLInputElement>(null);
  const { currentOrg, updateOrgInfo } = useCurrentOrg();
  const fileChooserInputRef = useRef<HTMLInputElement>(null);
  const [fileToUpload, setFileToUpload] = useState<File | undefined>();
  const psalmNumberInputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<UploadStatus>(UploadStatus.Before);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [isUploadSuccessful, setIsUploadSuccessful] = useState(false);
  const hymn = getHymnFromSlug({ songSlug, customMusic });
  const [songName, setSongName] = useState(() => hymn?.title);
  const songNameIsValid = !songName?.includes('/');
  let hymnPsalmNumber: number | undefined;
  {
    const { number } = hymn?.psalm?.match(/(?<number>\d+)/)?.groups ?? {};
    hymnPsalmNumber = number ? Number(number) : undefined;
  }
  const [psalmNumber, setPsalmNumber] = useState<number | undefined>(() => hymnPsalmNumber);
  const [showSpinner, setShowSpinner] = useState(false);
  const isValidPsalmNumber = psalmNumber === undefined || (psalmNumber >= 1 && psalmNumber <= 150);
  const [isHiddenSong, setIsHiddenSong] = useState(() => !!currentOrg?.hiddenSongs?.includes(songSlug));
  const { setSongVisibility } = useSongVisibility();

  // the practice video link fields; the videos they hold are saved on Done
  const { videoFields, setVideoField, editedVideos } = usePracticeVideoFields(currentOrg?.practiceVideos?.[songSlug]);
  const baseId = useId();
  const ids = {
    name: `${baseId}-name`,
    psalm: `${baseId}-psalm`,
    hide: `${baseId}-hide`,
  };

  // A reload would take this dialog's unsaved name and psalm number away, and would abort an upload
  // mid-flight (or skip the manifest bookkeeping that follows it), so hold the app reload off for as
  // long as either is true - the upload outlives the dialog when the user cancels (issue #3265).
  // the dialog holds the reload while it is open; the request below holds it while in flight,
  // because cancelling hides the dialog (and unmounts this) while the upload keeps going
  useHoldAppReload(show);

  useEffect(() => {
    songNameInputRef.current?.focus();
  }, [songNameInputRef, setPsalmNumber, songName]);

  const onSongNameChanged = useCallback(() => {
    setSongName(songNameInputRef.current?.value ?? '');
  }, [songNameInputRef, setSongName]);

  const onToggleHidden = useCallback(() => {
    const newValue = !isHiddenSong;
    setIsHiddenSong(newValue);
    void setSongVisibility({ songSlug, isVisible: !newValue });
  }, [isHiddenSong, setSongVisibility, songSlug]);

  const onPsalmNumberChanged = useCallback(() => {
    const psalmNumber = Number(psalmNumberInputRef.current?.value);
    if (isNaN(psalmNumber) || psalmNumber === 0) {
      setPsalmNumber(undefined);
    } else {
      setPsalmNumber(psalmNumber);
    }
  }, [psalmNumberInputRef, setPsalmNumber]);

  // saves the edited videos to the org when they differ from what it has; independent of the name and psalm
  const saveVideos = useCallback(() => {
    if (!currentOrg || isSamePracticeVideos(currentOrg.practiceVideos?.[songSlug], editedVideos)) {
      return;
    }
    // updateOrgInfo writes the cached org before it syncs, so the dialog can close without waiting on the network
    void updateOrgInfo({
      id: currentOrg.id,
      practiceVideos: withSongPracticeVideos(currentOrg.practiceVideos, songSlug, editedVideos),
    });
  }, [currentOrg, editedVideos, songSlug, updateOrgInfo]);

  const onClickUpload = useCallback(() => {
    if (!songNameIsValid) {
      void alert({ confirmation: <span>Song name may not contain a slash (/)</span> });
      return;
    }
    const number = songName?.match(/^Psalm (?<number>\d+)/)?.groups?.number;
    if (number && !psalmNumber) {
      setPsalmNumber(Number(number));
    }
    fileChooserInputRef?.current?.click();
  }, [psalmNumber, setPsalmNumber, songName, songNameIsValid]);

  const onDelete = useCallback(() => {
    if (!currentOrg?.id) {
      return;
    }
    void (async () => {
      if (
        !(await confirm({
          confirmation: (
            <span>Press Confirm to delete {hymn?.title ? `'${hymn.title}'` : 'song'} from your hymnal</span>
          ),
          confirmButtonText: 'Confirm',
          cancelButtonText: 'Cancel',
        }))
      ) {
        return;
      }
      setShowSpinner(true);
      try {
        await setSongVisibility({ songSlug, isVisible: true });
        await server_api.deleteCustomSong(songSlug);
        await recordRecentlyDeletedSong(customMusicManifest, currentOrg.id, songSlug);
        // the server dropped the song's practice videos with it; drop them from the cached org too
        if (currentOrg.practiceVideos?.[songSlug]) {
          const { [songSlug]: _deleted, ...otherSongs } = currentOrg.practiceVideos;
          await updateOrgInfo({ id: currentOrg.id, practiceVideos: otherSongs });
        }
        onClose();
      } catch (e: any) {
        setShowSpinner(false);
        await alert({ confirmation: <span>Error: {e.message}</span> });
      } finally {
        setShowSpinner(false);
      }
    })();
  }, [currentOrg, customMusicManifest, hymn, onClose, setSongVisibility, songSlug, updateOrgInfo]);

  const onFileChosen = useCallback(() => {
    const file = fileChooserInputRef?.current?.files?.[0];
    if (!file || !currentOrg || !songName) {
      console.log('no file');
      return;
    }
    setFileToUpload(file);
  }, [currentOrg, songName]);

  const onRetry = useCallback(() => {
    setStatus(UploadStatus.Before);
    setErrorMessage(undefined);
    setFileToUpload(undefined);
    setIsUploadSuccessful(false);
  }, []);

  const onFinish = useCallback(() => {
    if (!currentOrg || !songName || !songNameIsValid || !isValidPsalmNumber || !hymn) {
      if (!currentOrg) {
        console.error(`Missing org`);
        onClose();
      }
      if (!hymn) {
        console.error(`Missing hymn`);
        onClose();
      }
      if (!songName) {
        void alert({ confirmation: <span>Missing song name</span> });
      }
      if (!songNameIsValid) {
        void alert({ confirmation: <span>Song name may not contain a slash (/)</span> });
        return;
      }
      if (!isValidPsalmNumber) {
        void alert({ confirmation: <span>Invalid Psalm number</span> });
      }
      return;
    }
    saveVideos();
    const anythingChanged = songName !== hymn.title || psalmNumber !== hymnPsalmNumber;
    if (!anythingChanged) {
      onClose();
      return;
    }
    void (async () => {
      setShowSpinner(true);
      try {
        await server_api.renameCustomSong({ songSlug, songName, psalmNumber });
        await recordRecentlyAddedSong({
          customMusicManifest,
          org: currentOrg,
          songSlug,
          songName,
          psalmNumber,
        });
        onClose();
      } catch (e: any) {
        setShowSpinner(false);
        await alert({ confirmation: <span>Error: {e.message}</span> });
      } finally {
        setShowSpinner(false);
      }
    })();
  }, [
    currentOrg,
    songName,
    songNameIsValid,
    isValidPsalmNumber,
    hymn,
    saveVideos,
    psalmNumber,
    hymnPsalmNumber,
    onClose,
    songSlug,
    customMusicManifest,
  ]);

  const uploadFile = useCallback(async () => {
    if (busyUploading) {
      return;
    }
    busyUploading = true;
    try {
      if (
        status !== UploadStatus.Before ||
        !fileToUpload ||
        !currentOrg ||
        !songName ||
        isUploadSuccessful ||
        errorMessage
      ) {
        return;
      }
      setStatus(UploadStatus.Uploading);
      const releaseAppReloadHold = holdAppReload();
      try {
        const songNumber = songName && (await songNumberToOverwrite(customMusic[currentOrg.id], songName, psalmNumber));
        const newlyAssignedSongNumber = await server_api.uploadOrgCustomMusicFile({
          orgId: currentOrg.id,
          songName,
          psalmNumber,
          file: fileToUpload,
          songNumber,
        });
        console.log(newlyAssignedSongNumber);
        // setAssignedSongNumber(newlyAssignedSongNumber);
        setStatus(UploadStatus.Succeeded);
      } catch (error: any) {
        setErrorMessage(error.message);
        setStatus(UploadStatus.Failed);
      }
      releaseAppReloadHold();
      setFileToUpload(undefined);
      setIsUploadSuccessful(true);
    } finally {
      busyUploading = false;
    }
  }, [currentOrg, customMusic, errorMessage, fileToUpload, isUploadSuccessful, psalmNumber, songName, status]);

  useEffect(() => {
    void uploadFile();
  }, [uploadFile]);

  const onClickDialog = useCallback(
    (e: any) => {
      const didClickBackdrop = e.currentTarget === e.target;
      if (didClickBackdrop) {
        onClose();
      }
    },
    [onClose],
  );

  return (
    <Dialog key="dialog" ref={dialogRef} onClick={onClickDialog} open={isInline || undefined} $isInline={isInline}>
      {fileToUpload || isUploadSuccessful ? (
        <UploadContent>
          {status === UploadStatus.Before || status === UploadStatus.Uploading ? (
            <>
              <Message $widthInChars={16}>Uploading file</Message>
              <Spinner />
            </>
          ) : status === UploadStatus.Failed ? (
            <Message $widthInChars={23}>
              File upload failed.
              <br />
              <br />
              {errorMessage ?? ''}
            </Message>
          ) : (
            <Message $widthInChars={23}>
              File upload successful!
              <br />
              <br />
            </Message>
          )}
          {status === UploadStatus.Before || status === UploadStatus.Uploading ? (
            <Button onClick={onClose}>CANCEL</Button>
          ) : status === UploadStatus.Failed ? (
            <>
              <Button onClick={onRetry}>RETRY</Button>
              <Button onClick={onClose}>CANCEL</Button>
            </>
          ) : (
            <Button onClick={onFinish}>DONE</Button>
          )}
        </UploadContent>
      ) : (
        <Card>
          <input type="file" style={{ display: 'none' }} ref={fileChooserInputRef} onChange={onFileChosen} />
          <Header>
            <Title>Edit song</Title>
            <CloseButton type="button" aria-label="Close" onClick={onClose}>
              <Close aria-hidden="true" />
            </CloseButton>
          </Header>

          <NameAndPsalm>
            <NameField>
              <FieldLabel htmlFor={ids.name}>Name</FieldLabel>
              <TextField
                id={ids.name}
                type="text"
                onChange={onSongNameChanged}
                ref={songNameInputRef}
                defaultValue={songName}
                $isInvalid={!songNameIsValid}
              />
            </NameField>
            <PsalmField>
              <FieldLabel htmlFor={ids.psalm}>Psalm</FieldLabel>
              <TextField
                id={ids.psalm}
                type="number"
                placeholder="none"
                title={isValidPsalmNumber ? undefined : 'Invalid psalm number'}
                onChange={onPsalmNumberChanged}
                defaultValue={psalmNumber}
                ref={psalmNumberInputRef}
                $isInvalid={!isValidPsalmNumber}
                $isNarrow={true}
              />
            </PsalmField>
          </NameAndPsalm>

          <PracticeVideosSection videoFields={videoFields} setVideoField={setVideoField} editedVideos={editedVideos} />

          <HideRow>
            <HideLabel htmlFor={ids.hide}>Hide in &quot;Our Hymnal&quot;</HideLabel>
            <SwitchTrack
              id={ids.hide}
              type="button"
              role="switch"
              aria-checked={isHiddenSong}
              onClick={onToggleHidden}
              $on={isHiddenSong}
            >
              <SwitchKnob $on={isHiddenSong} />
            </SwitchTrack>
          </HideRow>

          <DoneButton type="button" onClick={onFinish}>
            Done
          </DoneButton>

          <SongActions>
            <SongActionButton type="button" onClick={onClickUpload}>
              <UploadIcon />
              Replace PDF
            </SongActionButton>
            <SongActionDivider />
            <SongActionButton type="button" onClick={onDelete} $isDangerous={true}>
              <Trash aria-hidden="true" />
              Delete song
            </SongActionButton>
          </SongActions>
        </Card>
      )}
      {showSpinner && <ModalSpinner />}
    </Dialog>
  );
}

const UploadIcon = () => (
  <svg
    aria-hidden="true"
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 16V4m0 0L7 9m5-5l5 5M4 20h16" />
  </svg>
);

const Dialog = styled.dialog<{ $isInline: boolean }>`
  box-sizing: border-box;
  width: calc(100% - 32px);
  max-width: 358px;
  border: none;
  border-radius: 20px;
  margin: auto;
  padding: 20px;
  background: var(--color-background);
  color: var(--color-text);
  font-family: Jost-Regular, Arial, sans-serif;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
  ${({ $isInline }) => ($isInline ? 'position: static; width: 358px;' : '')}

  &::backdrop {
    background: var(--color-dialog-backdrop, rgba(0, 0, 0, 0.45));
  }
`;

const UploadContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  gap: 20px;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 20px;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.h2`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 17px;
  font-weight: 600;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 16px;
  background: var(--color-close-button-background);
  color: var(--color-text);
  cursor: pointer;
`;

const NameAndPsalm = styled.div`
  display: flex;
  gap: 10px;
`;

const NameField = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PsalmField = styled.div`
  flex-shrink: 0;
  width: 72px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldLabel = styled.label`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-label);
`;

const TextField = styled.input<{ $isInvalid: boolean; $isNarrow?: boolean }>`
  box-sizing: border-box;
  width: 100%;
  height: 40px;
  padding: 0 ${({ $isNarrow }) => ($isNarrow ? 10 : 12)}px;
  border: 1.5px solid ${({ $isInvalid }) => ($isInvalid ? 'var(--color-invalid-input)' : 'var(--color-field-border)')};
  border-radius: 10px;
  font-family: Jost-Regular, Arial, sans-serif;
  font-size: 15px;
  color: var(--color-text);
  background: var(--color-background);
  outline: none;

  &:focus {
    border-color: var(--color-nightingale);
  }
`;

const HideRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2px 0;
`;

const HideLabel = styled.label`
  font-size: 14px;
  cursor: pointer;
`;

// the track and knob of the sing-along switch (learn_sing_along_switch.tsx), at the design's size
const switchWidth = 44;
const switchHeight = 26;
const knobSize = 20;
const knobInset = (switchHeight - knobSize) / 2;

const SwitchTrack = styled.button<{ $on: boolean }>`
  position: relative;
  flex: none;
  width: ${switchWidth}px;
  height: ${switchHeight}px;
  padding: 0;
  border: 0;
  border-radius: ${switchHeight / 2}px;
  background: ${({ $on }) => ($on ? 'var(--color-nightingale)' : 'var(--color-field-border)')};
  cursor: pointer;
  transition: background-color 120ms linear;
  -webkit-tap-highlight-color: transparent;
`;

const SwitchKnob = styled.span<{ $on: boolean }>`
  position: absolute;
  left: ${knobInset}px;
  top: ${knobInset}px;
  width: ${knobSize}px;
  height: ${knobSize}px;
  border-radius: 50%;
  background: ${({ $on }) => ($on ? 'var(--color-background-white)' : 'var(--color-background)')};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  transform: translateX(${({ $on }) => ($on ? switchWidth - knobSize - 2 * knobInset : 0)}px);
  transition: transform 120ms linear;
`;

const DoneButton = styled.button`
  height: 44px;
  border: 0;
  border-radius: 12px;
  background: var(--color-primary-button);
  color: var(--color-text-white);
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;
`;

const SongActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 22px;
  margin-top: -4px;
`;

const SongActionButton = styled.button<{ $isDangerous?: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  height: 32px;
  padding: 0 4px;
  border: 0;
  background: transparent;
  color: ${({ $isDangerous }) => ($isDangerous ? 'var(--color-text-dangerous)' : 'var(--color-nightingale)')};
  font-family: Jost-Regular, Arial, sans-serif;
  font-size: 13px;
  cursor: pointer;
`;

// the assets fill their boxes more than the design's icons did, so they are drawn smaller to look the same size
const Close = styled(CloseIcon)`
  width: 14px;
  height: 14px;
  fill: currentColor;
`;

const Trash = styled(TrashIcon)`
  width: 10px;
  height: 10px;
  margin: 1.5px;
  fill: currentColor;
`;

const SongActionDivider = styled.div`
  width: 1px;
  height: 14px;
  background: var(--color-field-border);
`;

async function songNumberToOverwrite(
  customMusic: Hymn[],
  songName: string,
  psalmNumber?: number,
): Promise<string | undefined> {
  if (!customMusic) {
    return undefined;
  }

  for (const hymn of customMusic) {
    const hymnPsalmNumber = hymn.psalm?.match(/(?<number>\d+)/)?.groups?.number;
    if (
      hymn.title.toLowerCase() === songName.toLowerCase() &&
      !!hymn.psalm === !!psalmNumber &&
      (!psalmNumber || psalmNumber === Number(hymnPsalmNumber))
    ) {
      if (
        await confirm({
          confirmation: <span>Would you like to overwrite the existing song with the same name?</span>,
        })
      ) {
        return hymn.songNumber;
      }
      break;
    }
  }
}

const editCustomSong = createConfirmation(confirmable(EditCustomSong));

export function EditSongButton({ songSlug }: { songSlug: SongSlug }) {
  const { customMusic, customMusicManifest } = useCustomMusic();
  const { hasPermission } = use(EditButtonContext) ?? {};
  const onClick = useCallback(
    async (event: any) => {
      event.stopPropagation();
      await editCustomSong({ customMusic, customMusicManifest, songSlug });
    },
    [customMusic, customMusicManifest, songSlug],
  );
  if (!hasPermission || !isCustomSong(songSlug)) {
    return;
  }
  return <Pencil style={{ width: '27px', height: '27px' }} onClick={onClick} />;
}
